import React, { useEffect, useState } from "react";
import { TrendingUp, RefreshCw, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";

export default function Trending() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/trends/sg").then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="container-rp section text-slate-400">Loading Singapore search trends…</div>;

  return (
    <div data-testid="page-trending">
      <SEO
        title="Singapore Risk Trends · live Google search interest"
        description="Live Google Trends for Singapore risk, compliance and regulatory keywords. See what MAS, PDPA, CCoP 2.0 and AML topics are heating up in SG search this quarter."
        keywords={["Singapore search trends", "MAS trends", "PDPA Singapore trends", "compliance search Singapore", "Google Trends SG GRC"]}
        path="/trending"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "RiskPedia SG · Singapore Risk Search Trends",
          description: "Quarterly Google Trends data for Singapore risk and compliance keywords.",
          keywords: data.trends?.map(t => t.keyword).slice(0, 10).join(", "),
          spatialCoverage: { "@type": "Country", name: "Singapore" },
        }}
      />

      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Live · Google Trends · Singapore</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">What Singapore is<br /><span style={{ color: "var(--rp-accent)" }}>searching for.</span></h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">Trade-weighted search interest across Singapore for 15 risk, compliance and regulatory keywords. Refreshed every 12 hours. Use it to brief your team on what's heating up in the SG GRC discourse — before the next MAS circular drops.</p>
          <div className="mt-6 text-xs font-mono text-slate-500">
            Source: {data.source === "google_trends_sg" ? "Google Trends · SG · last 90 days" : "Indicative fallback (quota limit)"} · cached {new Date(data.cached_at).toLocaleString()}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="overline">Top {data.trends?.length || 0} keywords by SG search interest</div>
          <h2 className="font-display text-3xl font-semibold mt-3">Ranked.</h2>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {(data.trends || []).map((t, idx) => (
              <div key={t.keyword} className="rp-surface p-6" data-testid={`trend-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-baseline gap-3">
                    <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-accent)" }}>{(idx + 1).toString().padStart(2, "0")}</div>
                    <div>
                      <div className="font-display text-lg font-semibold">{t.keyword}</div>
                      <div className="text-[11px] font-mono text-slate-400 mt-0.5">SG · 90 day interest</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-primary)" }}>{t.latest_score}</div>
                    <div className="text-[10px] font-mono text-slate-400">/100</div>
                  </div>
                </div>
                {/* sparkline */}
                {t.series?.length > 0 && (
                  <div className="mt-4 h-10 flex items-end gap-px">
                    {t.series.map((p, i) => {
                      const vals = t.series.map(x => x.value);
                      const min = Math.min(...vals), max = Math.max(...vals);
                      const range = Math.max(1, max - min);
                      const h = 4 + ((p.value - min) / range) * 32;
                      return <div key={i} className="flex-1" style={{ height: `${h}px`, background: "var(--rp-accent)", opacity: 0.35 + (i / t.series.length) * 0.55 }} />;
                    })}
                  </div>
                )}
                {data.related_rising?.[t.keyword]?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="overline-muted">Rising queries</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {data.related_rising[t.keyword].slice(0, 5).map(q => (
                        <span key={q} className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>{q}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 rp-card p-6 max-w-2xl">
            <div className="overline flex items-center gap-2"><TrendingUp size={11} /> Methodology</div>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">Data via <a href="https://pytrends.readthedocs.io/" target="_blank" rel="noopener noreferrer" className="underline">pytrends</a> (the same library used by riskpedia.co.in). 15 Singapore-localised keywords queried with <code className="text-xs">geo=SG</code> over a rolling 90-day window. Scores are 0–100 relative to peak interest. Cached every 12 hours.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
